import {
	CreateDrawerFormProps,
	FormDrawerContentProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nTextField,
	UpdateDrawerFormProps,
	useDynamicConfig,
	validateEachLanguage,
} from "@dgs/core";
import { FormikErrors } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { regionService } from "../shared/api/regionService";
import { RegionFormValues } from "../shared/types/region";
import { UserMultiSelectField } from "../users/UserSelectField";

const useValidate = () => {
	const { t } = useTranslation();

	return (values: RegionFormValues) => {
		const errors: FormikErrors<RegionFormValues> = {};
		return {
			...errors,
			...validateEachLanguage<RegionFormValues["translations"][number]>(values.translations, (_, translation) => {
				const errors: Record<string, any> = {};
				if (!translation.name) {
					errors.name = t("Field is required");
				}
				return errors;
			}),
		};
	};
};

const emptyTranslation = { name: "" };

const RegionFormDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();

	return (
		<I18nFormDrawerContent {...props} emptyTranslation={emptyTranslation}>
			<I18nTextField name="name" label={t("Name")} required="onlyOnMainForm" />
			<I18nOnlyOnMainForm>
				<UserMultiSelectField
					name="users"
					label={t("Approving users")}
					description={t("The users selected here can be chosen as approvers in the Event Hub.")}
				/>
			</I18nOnlyOnMainForm>
		</I18nFormDrawerContent>
	);
};

export const RegionCreateDrawer: FC<CreateDrawerFormProps<RegionFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const { languageSettings } = useDynamicConfig();

	const initialValues: RegionFormValues = useMemo(
		() => ({
			id: -1,
			translations: [{ locale: languageSettings.fallback, name: "" }],
			users: [],
		}),
		[languageSettings.fallback],
	);

	return (
		<I18nFormDrawer
			{...props}
			initialValues={initialValues}
			title={t("Create region")}
			validate={validate}
			renderForm={(formProps) => <RegionFormDrawerContent {...formProps} {...props} />}
		/>
	);
};

export const RegionUpdateDrawer: FC<UpdateDrawerFormProps<RegionFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<I18nFormFetchDrawer
			{...props}
			title={t("Edit region")}
			queryFn={() => regionService.show(props.id).then((x) => x.data.data)}
			queryKey={regionService.showKey(props.id)}
			validate={validate}
			renderSuccess={(formProps) => <RegionFormDrawerContent {...props} {...formProps} />}
		/>
	);
};
